(() => {
  const STORAGE_KEY = 'dnature:wishlist:v1';

  const selectors = {
    open: '.js-dnature-wishlist-open',
    toggle: '.js-dnature-wishlist-toggle',
    overlay: '.dnature-wishlist__overlay',
    drawer: '.dnature-wishlist',
    items: '.dnature-wishlist__items',
    empty: '.dnature-wishlist__empty',
    close: '.js-dnature-wishlist-close',
  };

  const normalizeHandle = (h) => (h || '').toString().trim();

  const read = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = JSON.parse(raw || '[]');
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const write = (arr) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch {
      // ignore
    }
  };

  const has = (handle) => read().includes(normalizeHandle(handle));

  const add = (handle) => {
    handle = normalizeHandle(handle);
    if (!handle) return;
    const list = read();
    if (!list.includes(handle)) {
      list.unshift(handle);
      write(list);
    }
  };

  const remove = (handle) => {
    handle = normalizeHandle(handle);
    if (!handle) return;
    const list = read().filter((h) => h !== handle);
    write(list);
  };

  const toggle = (handle) => {
    handle = normalizeHandle(handle);
    if (!handle) return false;
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle);
    return true;
  };

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const getDrawerEls = () => {
    return {
      overlay: $(selectors.overlay),
      drawer: $(selectors.drawer),
      items: $(selectors.items),
      empty: $(selectors.empty),
    };
  };

  const setBodyLock = (locked) => {
    document.documentElement.classList.toggle('overflow-hidden', locked);
  };

  const openDrawer = () => {
    const { overlay, drawer } = getDrawerEls();
    if (!overlay || !drawer) return;
    overlay.classList.add('is-open');
    drawer.classList.add('is-open');
    setBodyLock(true);
    renderDrawer();
  };

  const closeDrawer = () => {
    const { overlay, drawer } = getDrawerEls();
    if (!overlay || !drawer) return;
    overlay.classList.remove('is-open');
    drawer.classList.remove('is-open');
    setBodyLock(false);
  };

  const isDrawerOpen = () => {
    const { drawer } = getDrawerEls();
    return !!drawer && drawer.classList.contains('is-open');
  };

  const updateButtons = () => {
    const list = read();
    $$(`[data-dnature-wishlist-handle]`).forEach((btn) => {
      const handle = normalizeHandle(btn.getAttribute('data-dnature-wishlist-handle'));
      const active = !!handle && list.includes(handle);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const headerIcon = $('.js-dnature-wishlist-open');
    if (headerIcon) headerIcon.classList.toggle('is-active', list.length > 0);
  };

  const fetchProduct = async (handle) => {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error('Failed product fetch');
    return res.json();
  };

  const renderDrawer = async () => {
    const { items, empty } = getDrawerEls();
    if (!items || !empty) return;

    const list = read();
    items.innerHTML = '';

    if (!list.length) {
      empty.style.display = 'block';
      return;
    }

    empty.style.display = 'none';

    const fragments = [];

    for (const handle of list) {
      try {
        const p = await fetchProduct(handle);
        const img = (p.featured_image || '').replace(/\\?.*$/, '');
        const safeTitle = (p.title || '').toString();
        const url = p.url || `/products/${handle}`;

        fragments.push(`
          <div class="dnature-wishlist__item" data-handle="${handle}">
            <a class="dnature-wishlist__item-link" href="${url}">
              <img class="dnature-wishlist__item-img" src="${img}" alt="${safeTitle.replace(/\"/g, '&quot;')}" loading="lazy">
            </a>
            <a class="dnature-wishlist__item-link" href="${url}">
              <p class="dnature-wishlist__item-title">${safeTitle}</p>
            </a>
            <button class="dnature-wishlist__remove js-dnature-wishlist-remove" type="button" data-handle="${handle}">Remove</button>
          </div>
        `);
      } catch {
        fragments.push(`
          <div class="dnature-wishlist__item" data-handle="${handle}">
            <div>
              <p class="dnature-wishlist__item-title">${handle}</p>
            </div>
            <div></div>
            <button class="dnature-wishlist__remove js-dnature-wishlist-remove" type="button" data-handle="${handle}">Remove</button>
          </div>
        `);
      }
    }

    items.innerHTML = fragments.join('');
  };

  const onClick = (e) => {
    const openBtn = e.target.closest(selectors.open);
    if (openBtn) {
      e.preventDefault();
      openDrawer();
      return;
    }

    const closeBtn = e.target.closest(selectors.close);
    if (closeBtn) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    const overlay = e.target.closest(selectors.overlay);
    if (overlay && overlay.classList.contains('is-open')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    const removeBtn = e.target.closest('.js-dnature-wishlist-remove');
    if (removeBtn) {
      e.preventDefault();
      const handle = removeBtn.getAttribute('data-handle');
      remove(handle);
      updateButtons();
      renderDrawer();
      return;
    }

    const toggleBtn = e.target.closest(selectors.toggle);
    if (toggleBtn) {
      e.preventDefault();
      e.stopPropagation();
      const handle = toggleBtn.getAttribute('data-dnature-wishlist-handle');
      toggle(handle);
      updateButtons();
      if (isDrawerOpen()) renderDrawer();
      return;
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    if (!isDrawerOpen()) return;
    closeDrawer();
  };

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeyDown);

  document.addEventListener('DOMContentLoaded', () => {
    updateButtons();
  });

  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    updateButtons();
    if (isDrawerOpen()) renderDrawer();
  });
})();
